/**
 * Derive checklist sub-items for a STANDALONE harness task.
 *
 * Missions get their steps for free — the DAG plan enumerates them up
 * front. A directly-fired harness has no such plan: harn-service keeps
 * only the LATEST `{step, pct, message}` frame on the task record. The
 * sidebar redesign handoff (2026-07-19 §4) names this as the one honest
 * gap and offers two ways to close it; this is **option (a)** — derive
 * the steps from the persisted progress-frame history at
 * `GET /harn/tasks/:id/logs?type=progress`, which needs no runtime
 * change.
 *
 * The derivation rule: each DISTINCT `step` token, in first-seen order,
 * becomes one child. Every step before the last is `done`; the last one
 * is `running` while the task is live (carrying its newest message/pct)
 * and `done`/`failed` once the task is terminal.
 *
 * Honest limits, by construction:
 *   - This is a REAR-VIEW list. Unlike a mission plan it cannot show
 *     steps the harness has not reached yet, so a running harness shows
 *     no `☐ pending` children. Callers must not imply completeness.
 *   - A harness that emits no progress frames (or a deployment with no
 *     log sink) yields an EMPTY list, and the row correctly renders with
 *     no children rather than inventing any.
 *
 * Pure + IO-free so it unit-tests without a server. If the extra fetch
 * or this rear-view limitation becomes a problem, the runtime lane has
 * offered option (b): a capped `progressHistory` on the task record.
 */
import type { HarnTaskLogEntry, HarnTaskStatus } from "@/api/lx-harn";
import { normalizeProgressPct } from "@/lib/agents/progress-pct";

export type HarnessStepStatus = "done" | "running" | "failed";

export interface HarnessStep {
  /** Distinct `step` token — also the React key. */
  step: string;
  status: HarnessStepStatus;
  /** Newest human message seen for this step, when the harness sent one. */
  message?: string;
  /** Newest pct seen for this step (0-100), when the harness sent one. */
  pct?: number;
  /** Timestamp of the first frame for this step. */
  at?: number;
}

/** Normalise the three frame shapes the SDK has emitted over time into
 *  one `{step, pct, message, ts}` view. Returns null for non-progress or
 *  malformed lines. */
function readFrame(
  entry: HarnTaskLogEntry,
): { step: string; pct?: number; message?: string; ts?: number } | null {
  if (!entry || typeof entry !== "object") return null;
  // A `type` field, when present, must say this is a progress frame.
  if (typeof entry.type === "string" && entry.type !== "progress") return null;
  const inner = entry.progress ?? entry.fields ?? entry;
  const step = (inner as { step?: unknown }).step;
  if (typeof step !== "string" || step.trim() === "") return null;
  const pctRaw = (inner as { pct?: unknown }).pct;
  const messageRaw = (inner as { message?: unknown }).message;
  const ts = typeof entry.ts === "number" ? entry.ts : entry.time;
  return {
    step: step.trim(),
    pct:
      typeof pctRaw === "number"
        ? (normalizeProgressPct(pctRaw) ?? undefined)
        : undefined,
    message:
      typeof messageRaw === "string" && messageRaw.trim() !== ""
        ? messageRaw.trim()
        : undefined,
    ts: typeof ts === "number" ? ts : undefined,
  };
}

export interface DeriveHarnessStepsOptions {
  /** The task's current status — decides how the LAST step renders. */
  status: HarnTaskStatus;
  /** Cap on the number of children rendered. The tail is what matters
   *  (it is the most recent work), so the OLDEST steps are dropped when
   *  a chatty harness exceeds this. */
  limit?: number;
}

export const HARNESS_STEPS_LIMIT = 12;

/**
 * Build the ordered, deduped checklist for one harness task.
 * Never throws: a malformed log stream degrades to fewer/no children.
 */
export function deriveHarnessSteps(
  entries: HarnTaskLogEntry[] | undefined | null,
  opts: DeriveHarnessStepsOptions,
): HarnessStep[] {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const byStep = new Map<string, HarnessStep>();
  for (const entry of entries) {
    const frame = readFrame(entry);
    if (!frame) continue;
    const existing = byStep.get(frame.step);
    if (existing) {
      // Newest frame wins for the live fields; first-seen order and
      // timestamp are preserved.
      if (frame.message !== undefined) existing.message = frame.message;
      if (frame.pct !== undefined) existing.pct = frame.pct;
      continue;
    }
    byStep.set(frame.step, {
      step: frame.step,
      status: "done",
      message: frame.message,
      pct: frame.pct,
      at: frame.ts,
    });
  }
  const steps = [...byStep.values()];
  if (steps.length === 0) return [];

  // The final step carries the task's own outcome; everything before it
  // is necessarily finished (the harness moved past it).
  const last = steps[steps.length - 1];
  if (last) {
    if (opts.status === "failed") {
      last.status = "failed";
    } else if (isLiveStatus(opts.status)) {
      last.status = "running";
    } else {
      last.status = "done";
      // A completed task's last frame is history, not live telemetry —
      // a lingering "60%" beside a ✓ would misreport it.
      last.pct = undefined;
    }
  }

  const limit = opts.limit ?? HARNESS_STEPS_LIMIT;
  return steps.length > limit ? steps.slice(steps.length - limit) : steps;
}

function isLiveStatus(status: HarnTaskStatus): boolean {
  return (
    status === "running" || status === "dispatched" || status === "queued"
  );
}
